import { Delete as DeleteIcon, Edit as EditIcon } from "@mui/icons-material";
import { Box, Checkbox, Chip, FormControlLabel } from "@mui/material";
import { format } from "date-fns";
import { t } from "i18next";
import { useState } from "react";
import CoreTableEnhanced from "../../../../app/core/components/CoreTable/CoreTableEnhanced";
import CoreModal from "../../../../app/core/components/CoreModal/CoreModal";
import DetailTask from "./DetailTask";
import CoreDialog from "../../../../app/core/components/CoreDialog/CoreDialog";

const PracticeTaskManager = ({
  tasks = [],
  totalTasks = 0,
  onAddTask,
  onUpdateTask,
  onDeleteTask,
  onToggleStatus,
  onLoadTasks, // Nuova prop per gestire paginazione e ordinamento
  readOnly = false,
  isLoading = false,
  pageSize = 10, // Dimensione pagina
  isTaskDetailOpen,
  setIsTaskDetailOpen,
}) => {
  const [editTask, setEditTask] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [currentTask, setCurrentTask] = useState(null);

  // Stati per paginazione e ordinamento
  const [page, setPage] = useState(1);
  const [orderDirection, setOrderDirection] = useState("asc");
  const [valueToOrderBy, setValueToOrderBy] = useState("name");

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    notes: "",
    startingDate: undefined,
    endDate: undefined,
    estimatedEffort: 0,
    actualEffort: 0,
  });

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      notes: "",
      startingDate: undefined,
      endDate: undefined,
      estimatedEffort: 0,
    });
  };

  // Gestione cambio pagina
  const handlePageChange = (newPage) => {
    setPage(newPage);
    if (onLoadTasks) {
      const startRow = (newPage - 1) * pageSize;
      const endRow = newPage * pageSize;
      onLoadTasks({
        startRow,
        endRow,
        sort: [
          {
            colId: valueToOrderBy,
            sortDirection: orderDirection,
            sortIndex: 0,
          },
        ],
      });
    }
  };

  // Gestione ordinamento
  const handleSortChange = (property) => {
    const isAscending = valueToOrderBy === property && orderDirection === "asc";
    const newDirection = isAscending ? "desc" : "asc";

    setValueToOrderBy(property);
    setOrderDirection(newDirection);

    if (onLoadTasks) {
      const startRow = (page - 1) * pageSize;
      const endRow = page * pageSize;
      onLoadTasks({
        startRow,
        endRow,
        sort: [
          {
            colId: property,
            sortDirection: newDirection,
            sortIndex: 0,
          },
        ],
      });
    }
  };

  const handleEditClick = (task) => {
    setCurrentTask(task);
    setEditTask(true);
    setFormData({
      name: task.name,
      description: task.description,
      notes: task.notes || "",
      startingDate: task.startingDate || undefined,
      endDate: task.endDate || undefined,
      estimatedEffort: task.estimatedEffort || 0,
      actualEffort: task.actualEffort || 0,
    });
    setIsTaskDetailOpen(true);
  };

  const handleDeleteClick = (task) => {
    setCurrentTask(task);
    setIsDeleteDialogOpen(true);
  };

  const handleSaveAdd = () => {
    if (onAddTask) {
      const { actualEffort, ...taskDataForCreation } = formData;
      onAddTask(taskDataForCreation);
    }
    setIsTaskDetailOpen(false);
    resetForm();
  };

  const handleSaveEdit = () => {
    if (onUpdateTask && currentTask) {
      onUpdateTask(currentTask.id, formData);
    }
    setIsTaskDetailOpen(false);
    setCurrentTask(null);
    resetForm();
  };

  const handleConfirmDelete = () => {
    if (onDeleteTask && currentTask) {
      onDeleteTask(currentTask.id);
    }
    setIsDeleteDialogOpen(false);
    setCurrentTask(null);
  };

  const handleStatusChange = (taskId, newStatus) => {
    if (onToggleStatus) {
      const status = newStatus ? "Complete" : "Pending";
      onToggleStatus(taskId, status);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return format(new Date(dateString), "dd/MM/yyyy");
    } catch {
      return "-";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Complete":
        return "success";
      case "Pending":
        return "warning";
      default:
        return "default";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "Complete":
        return t("Completed");
      case "Pending":
        return t("Pending");
      default:
        return t("Pending");
    }
  };

  const canEdit = (task) => !readOnly;
  const canDelete = (task) => !task.default && !readOnly;

  // Calcolo pagine totali
  const totalPages = Math.ceil(totalTasks / pageSize);

  // Definizione colonne per CoreTableEnhanced
  const columns = [
    { id: "status", label: t("Status") },
    { id: "name", label: t("Name") },
    // { id: "description", label: t("Description") },
    { id: "startingDate", label: t("StartDate") },
    { id: "endDate", label: t("EndDate") },
    { id: "estimatedEffort", label: t("EstimatedEffort") },
    { id: "actualEffort", label: t("ActualEffort") },
    // { id: "notes", label: t("Note") },
  ];

  // Preparazione dati per CoreTableEnhanced
  const tableRows = tasks.map((task) => ({
    id: task.id,
    status: (
      <FormControlLabel
        control={
          <Checkbox
            checked={task.status === "Complete"}
            onChange={(e) => handleStatusChange(task.id, e.target.checked)}
            disabled={readOnly || isLoading}
          />
        }
        label={
          <Chip
            label={getStatusLabel(task.status)}
            color={getStatusColor(task.status)}
            size="small"
          />
        }
      />
    ),
    name: (
      <Box display="flex" alignItems="center" gap={1}>
        {task.name}
        {task.default && (
          <Chip
            label={t("Default")}
            color="info"
            size="small"
            variant="outlined"
          />
        )}
      </Box>
    ),
    description: task.description,
    startingDate: formatDate(task.startingDate),
    endDate: formatDate(task.endDate),
    estimatedEffort: `${task.estimatedEffort || 0}h`,
    actualEffort: `${task.actualEffort || 0}h`,
    notes: task.notes || "-",
    originalTask: task,
  }));

  // Azioni per CoreTableEnhanced
  const tableActions = [
    {
      icon: <EditIcon />,
      tooltip: t("Edit"),
      onClick: (row) => handleEditClick(row.originalTask),
      disabled: (row) => !canEdit(row.originalTask) || isLoading,
    },
    {
      icon: <DeleteIcon />,
      tooltip: t("Delete"),
      onClick: (row) => handleDeleteClick(row.originalTask),
      disabled: (row) => !canDelete(row.originalTask) || isLoading,
    },
  ];

  const handleCloseTaskModal = () => {
    setIsTaskDetailOpen(false);
    resetForm();
  };

  return (
    <Box>
      <Box className="flex flex-col gap-y-2 m-2">
        <h3 className="m-0">{t("Tasks")}</h3>
      </Box>

      <CoreTableEnhanced
        rows={tableRows}
        columns={columns}
        button={!readOnly}
        actions={tableActions}
        isLoading={isLoading}
        idsSort={true}
        emptyMessage={t("NoTasks")}
        showPagination={true}
        stickyHeader={false}
        maxHeight={600}
        page={page}
        setPage={handlePageChange}
        totalPages={totalPages}
        orderDirection={orderDirection}
        setOrderDirection={setOrderDirection}
        valueToOrderBy={valueToOrderBy}
        setValueToOrderBy={handleSortChange}
      />

      <CoreModal
        component={
          <DetailTask
            task={formData}
            onInputChange={handleInputChange}
            onClose={handleCloseTaskModal}
            onLoading={isLoading}
            onSave={handleSaveAdd}
            onEdit={handleSaveEdit}
            editTask={editTask}
          />
        }
        state={isTaskDetailOpen}
        onClose={handleCloseTaskModal}
        width={1000}
        height={800}
      />
      <CoreDialog
        open={isDeleteDialogOpen}
        disabledButton={isLoading}
        title={t("DeleteTask")}
        content={` ${t("DeleteTaskConfirm")} "${currentTask?.name}"?`}
        negativeButton={t("No")}
        positiveButton={t("Yes")}
        handlePositive={handleConfirmDelete}
        closeDialog={() => setIsDeleteDialogOpen(false)}
      />
    </Box>
  );
};

export default PracticeTaskManager;
